"use client";

import { IconFlame } from "@tabler/icons-react";
import {
  capacityNoticeAmberSurfaceClassName,
  capacityNoticeIconClassName,
} from "@/components/capacity-notice-admonition";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/lib/auth";
import { capacityBlockedDetail } from "@/lib/capacity-status-detail";
import { cn } from "@/lib/utils";

type Variant = "global" | "inline";

/** Must match the global strip row height (used with `h-(--capacity-banner-offset)`). */
export const CAPACITY_BANNER_OFFSET = "1.875rem";

export const CapacityNoticeBanner = ({ variant = "global", className }: { variant?: Variant; className?: string }) => {
  const { capacityStatus } = useAuth();

  if (!capacityStatus || capacityStatus.state === "granted") {
    return null;
  }

  const state = capacityStatus.state;
  const title = state === "blocked_global" ? "Radium is at capacity" : "You are on the waitlist";
  const detail = capacityBlockedDetail(capacityStatus);
  const fullLabel = `${title} · ${detail}`;

  if (variant === "inline") {
    return (
      <Alert variant="warning" className={cn("mb-4", className)}>
        <IconFlame aria-hidden />
        <AlertDescription className="text-foreground/90 col-start-2 line-clamp-2 md:line-clamp-1 md:truncate">
          <span className="font-medium">{title}</span>
          <span className="text-foreground/85"> · {detail}</span>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div
      role="alert"
      className={cn(
        "relative z-30 flex h-(--capacity-banner-offset) w-full shrink-0 items-center justify-center gap-3 overflow-hidden border-b border-amber-500 px-4 text-[0.8125rem]",
        capacityNoticeAmberSurfaceClassName,
        className,
      )}
    >
      <IconFlame className={capacityNoticeIconClassName} aria-hidden />
      <p
        className="min-w-0 max-w-full text-center text-balance leading-snug line-clamp-2 md:line-clamp-1 md:truncate"
        title={fullLabel}
      >
        <span className="font-medium">{title}</span>
        <span className="text-amber-950/85"> · {detail}</span>
      </p>
    </div>
  );
};
